import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Badge,
    Button,
    Icon,
    Input,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "i18next";
import { DateTime } from "luxon";
import { useState } from "react";
import { TbDownload, TbEdit, TbPhone, TbTrash } from "react-icons/tb";
import { toast } from "sonner";

import { useDeletePhoneNumber, usePhoneNumbers } from "@/queries/phone-numbers.queries";
import { ApiPhoneNumber, IPhoneNumber, PhoneNumberStatus } from "@/types/phone.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { PhoneNumbersFormDialog } from "./components/phone-numbers-form-dialog";

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_PHONE_NUMBERS)({
    component: ManagementPhoneNumbers,
    staticData: {
        config: {
            breadcrumb: {
                title: "phone_numbers",
                to: APP_ROUTES_TO.MANAGEMENT_PHONE_NUMBERS,
            },
            title: "phone_numbers",
        },
    },
});

function ManagementPhoneNumbers() {
    const [filterName, setFilterName] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [editItem, setEditItem] = useState<ApiPhoneNumber | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [phoneNumberToDelete, setPhoneNumberToDelete] = useState<IPhoneNumber | null>(null);

    const { data: phoneNumbers = [], isLoading, refetch } = usePhoneNumbers();
    const deletePhoneNumberMutation = useDeletePhoneNumber();

    const handleFilterByName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterName(event.target.value);
    };

    const handleOpenDialog = () => {
        setEditItem(undefined);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditItem(undefined);
    };

    const handleEditItem = (item: IPhoneNumber) => {
        const apiPhoneNumber: ApiPhoneNumber = {
            id: item.id,
            phoneNumber: item.number,
            name: item.name || "",
            countries: item.countries || [],
            brands: item.brands || [],
            status: item.isActive ? PhoneNumberStatus.ACTIVE : PhoneNumberStatus.INACTIVE,
        };

        setEditItem(apiPhoneNumber);
        setOpenDialog(true);
    };

    const handleDialogSuccess = () => {
        refetch();
        toast.success("Operation completed successfully");
    };

    // Phone number deletion functions
    const handleOpenDeleteDialog = (phoneNumber: IPhoneNumber) => {
        setPhoneNumberToDelete(phoneNumber);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setPhoneNumberToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleDeletePhoneNumber = async () => {
        if (!phoneNumberToDelete) {
            handleCloseDeleteDialog();
            return;
        }

        try {
            await deletePhoneNumberMutation.mutateAsync(phoneNumberToDelete.id);
            toast.success("Phone number successfully deleted");
            refetch();
        } catch (error) {
            console.error("Error occurred while deleting phone number:", error);
            toast.error("Error occurred while deleting phone number");
        }

        handleCloseDeleteDialog();
    };

    // Filtering function
    const filteredPhoneNumbers = phoneNumbers.filter((phoneNumber: IPhoneNumber) => {
        const searchTerm = filterName.toLowerCase();
        return (
            phoneNumber.number.toLowerCase().includes(searchTerm) ||
            (phoneNumber.name || "").toLowerCase().includes(searchTerm)
        );
    });

    // Export to CSV function
    const handleExportData = () => {
        if (phoneNumbers.length === 0) {
            toast.error("No data found to export.");
            return;
        }

        const csvColumns = ["Number", "Name", "Status", "Brands", "Countries", "ID"];
        const csvData = phoneNumbers.map((item: IPhoneNumber) => [
            item.number,
            item.name || "-",
            item.isActive ? t("active") : t("inactive"),
            item.brands?.join(", ") || "-",
            item.countries?.join(", ") || "-",
            item.id,
        ]);

        const fileName = `phone-numbers-${DateTime.now().toFormat("yyyy-MM-dd_HH-mm")}`;

        // Manual CSV creation
        const header = csvColumns.join(",");
        const rows = csvData.map((row: string[]) => row.join(","));
        const csvContent = [header, ...rows].join("\n");

        // Download CSV
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        // Create anchor without adding to DOM - this avoids triggering LoadingScreen's click handler
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.csv`;
        link.click();

        // Clean up the object URL to prevent memory leaks
        setTimeout(() => URL.revokeObjectURL(url), 100);
    };

    return (
        <>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <h2 className="text-2xl font-bold tracking-tight">{t("phone_numbers")}</h2>

                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <Input
                        placeholder={t("search_by_name_number")}
                        value={filterName}
                        onChange={handleFilterByName}
                        className="w-full sm:w-[240px]"
                    />

                    <Button variant="outline" onClick={handleExportData}>
                        <Icon icon={TbDownload} className="mr-2" />
                        {t("export")}
                    </Button>

                    <Button onClick={handleOpenDialog}>
                        <Icon icon={TbPhone} className="mr-2" />
                        {t("new_number")}
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t("phone_number")}</TableHead>
                            <TableHead>{t("name")}</TableHead>
                            <TableHead>{t("countries")}</TableHead>
                            <TableHead>{t("brands")}</TableHead>
                            <TableHead>{t("status")}</TableHead>
                            <TableHead>{t("actions")}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell className="text-center col-span-6">{t("loading")}</TableCell>
                            </TableRow>
                        ) : filteredPhoneNumbers.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-center col-span-6">{t("no_phone_numbers_found")}</TableCell>
                            </TableRow>
                        ) : (
                            filteredPhoneNumbers.map((phoneNumber) => (
                                <TableRow key={phoneNumber.id}>
                                    <TableCell>{phoneNumber.number}</TableCell>
                                    <TableCell>{phoneNumber.name || "-"}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {phoneNumber.countries?.map((country) => (
                                                <Badge key={country} label={country} variant="outline" />
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {phoneNumber.brands
                                                ?.slice(0, 2)
                                                .map((brand) => (
                                                    <Badge key={brand} label={brand} variant="brand" colorKey={brand} />
                                                ))}
                                            {phoneNumber.brands && phoneNumber.brands.length > 2 && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Badge
                                                            label={`+${phoneNumber.brands.length - 2}`}
                                                            variant="outline"
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <div className="flex flex-wrap gap-1">
                                                            {phoneNumber.brands.slice(2).map((brand: string) => (
                                                                <Badge
                                                                    key={brand}
                                                                    label={brand}
                                                                    variant="brand"
                                                                    colorKey={brand}
                                                                />
                                                            ))}
                                                        </div>
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {phoneNumber.isActive ? (
                                            <Badge label={t("active")} variant="success" />
                                        ) : (
                                            <Badge label={t("inactive")} variant="danger" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-2">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleEditItem(phoneNumber)}
                                                    >
                                                        <Icon icon={TbEdit} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("edit")}</TooltipContent>
                                            </Tooltip>

                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleOpenDeleteDialog(phoneNumber)}
                                                    >
                                                        <Icon icon={TbTrash} color="red" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("delete")}</TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Delete confirmation dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("delete_phone_number")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("are_you_sure_you_want_to_delete_the_number", {
                                number: phoneNumberToDelete?.number,
                            })}
                            {t("this_action_cannot_be_undone")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCloseDeleteDialog}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeletePhoneNumber}
                            className="bg-destructive text-destructive-foreground"
                        >
                            {t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Form Dialog */}
            <PhoneNumbersFormDialog
                open={openDialog}
                onClose={handleCloseDialog}
                editItem={editItem}
                onSuccess={handleDialogSuccess}
            />
        </>
    );
}
